import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'next-i18next'
import styled from 'styled-components'
import { path } from 'ramda'
import ReactDOM from 'react-dom'
import { usePopper } from 'react-popper'
import { useDispatch, useSelector } from 'react-redux'

import ENV_VARS from '@/config'
import { breakpoints, colors, zIndex } from '@/styles'
import useMediaQuery from '@/hooks/use-media-query'
import useRole from '@/hooks/use-role'
import { show as showLoginModal } from '@/features/Auth/login-modal'
import Avatar from '@/components/avatar'
import useEventListener from '@/components/use-event-listener'
import useOnClickOutside from '@/components/use-onclick-outside'

import {
  renderAccountLabel,
  renderChatOption,
  renderCreditUsageOption,
  renderFavouritesOption,
  renderLandlordSettingsOption,
  renderListingsOption,
  renderLoginOption,
  renderLogoutOption,
  renderManageLabel,
  renderPricingOption,
  renderProfileOption,
  renderRentalsOption,
  renderSavedAlertsOption,
  renderSignupOption,
  renderTeamOption,
  renderTenantSettingsOption,
  renderTenantsOption,
  renderTrustScoreOption
} from './rendered-components'
import Sidebar from './sidebar'
import { StyledChatNotification } from './simple-components'
import { ReactComponent as HamburgerMenuIcon } from './menu-icon.svg'

const { DASHBOARD_URL, BASE_URL } = ENV_VARS

const Wrapper = styled.button`
  height: 46px;
  width: 86px;
  background: ${colors.grey96};
  padding: 8px 12px;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  border-radius: 64px;
  cursor: pointer;
  position: relative;

  p,
  svg,
  div {
    pointer-events: none;
  }

  &:disabled {
    pointer-events: none;
  }

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    height: 40px;
    width: 40px;
    padding: 8px;
  }
`

const Dropdown = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 250px;
  background-color: ${colors.white};
  border: 1px solid ${colors.grey88};
  border-radius: 6px;
  padding: 6px 0px 12px;
  box-shadow: 0px 10px 15px -3px rgba(0, 0, 0, 0.1),
    0px 4px 6px -2px rgba(0, 0, 0, 0.05);
  z-index: ${zIndex.dropdownMenu};
`

const PricingWrapper = styled.div`
  width: 100%;
  ${props => `
    @media (min-width: ${props.showSearch ? breakpoints.card : '866px'}) {
      display: none;
    }
  `}
`

const Divider = styled.div`
  border-bottom: 1px solid ${colors.grey88};
  width: 100%;
  margin: 4px 0;
`

const HeaderMenu = ({ hasSession, avatarUrl, showSearch }) => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const [stateOfSidebar, setStateOfSidebar] = useState('unmounted')
  const role = useRole()
  const session = useSelector(path(['session']))
  const base_plan_name = session?.company_info?.base_plan_name
  const hasEssentialsPlan =
    !base_plan_name || base_plan_name === 'essentials-plan-monthly'
  const dispatch = useDispatch()
  const displayMobile = useMediaQuery(`(max-width: ${breakpoints.ipadMiniMax})`)
  const aboveIpadMiniMax = useMediaQuery(
    `(min-width: ${breakpoints.ipadMiniMax})`
  )
  const isTenant = role === 'tenant'
  const isLoggedIn = !!role
  const [isMounted, setMounted] = useState(false)
  useEffect(() => {
    setMounted(true)
  }, [])
  const menuEventId = 'web-signup-submit-signupheader'

  const [referenceElement, setReferenceElement] = useState(null)
  const [popperElement, setPopperElement] = useState(null)
  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    strategy: 'fixed',
    placement: displayMobile ? 'bottom-end' : 'bottom-start',
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, 5]
        }
      }
    ]
  })

  const [hideTopNavbar, setHideTopNavbar] = useState(false)
  const [scrollPosition, setScrollPosition] = useState(0)
  const offset = 570

  const onScroll = useCallback(() => {
    setScrollPosition(window.pageYOffset)
    setHideTopNavbar(window.pageYOffset > offset)
  }, [offset])

  useEventListener('scroll', onScroll, {
    enabled: true
  })

  useEffect(() => {
    hideTopNavbar && setIsOpen(false)
  }, [hideTopNavbar, scrollPosition])

  useEventListener('scroll', onScroll, {
    enabled: true
  })

  useOnClickOutside({ current: popperElement }, () => setIsOpen(false))

  const handleClick = () => {
    setIsOpen(!isOpen)
  }

  const changeIsOpenToFalse = () => {
    setIsOpen(false)
  }

  const changeSetStateOfSidebar = useCallback(newValue => {
    setStateOfSidebar(newValue)
  }, [])

  const closeSidebar = () => {
    setStateOfSidebar('unmounting')
  }

  const savedAlertsClick = () => {
    showLoginModal({
      mode: 'registerSavedAlertsNav',
      eventId: 'web-signup-submit-savedalertbutton',
      t
    })
  }

  const loginClick = () => {
    setIsOpen(false)
    showLoginModal({
      eventId: menuEventId,
      t
    })
  }

  const loginClickRedirectChat = () => {
    setIsOpen(false)
    showLoginModal({
      afterLogin: () => {
        if (typeof window === 'undefined') return
        // eslint-disable-next-line react-compiler/react-compiler
        window.location.href = `${DASHBOARD_URL}/chat`
      },
      eventId: menuEventId,
      t
    })
  }

  const loginClickRedirectListings = () => {
    setIsOpen(false)
    showLoginModal({
      afterLogin: session => {
        if (typeof window === 'undefined') return

        if (session?.user_type === 'landlord') {
          // eslint-disable-next-line react-compiler/react-compiler
          window.location.href = `${DASHBOARD_URL}/listings`
        } else {
          // eslint-disable-next-line react-compiler/react-compiler
          window.location.href = `${BASE_URL}/rental-listings`
        }
      },
      eventId: menuEventId,
      t
    })
  }

  const signupClick = () => {
    setIsOpen(false)
    showLoginModal({
      mode: 'register',
      eventId: menuEventId,
      t
    })
  }

  const logoutClick = () => {
    setIsOpen(false)
    dispatch.auth.logout()
  }

  if (typeof window === 'undefined') return null

  const renderPublicMenu = () => {
    return (
      <>
        {renderManageLabel(t)}
        {renderChatOption(t, '', loginClickRedirectChat)}
        {renderListingsOption(t, '', loginClickRedirectListings)}
        {renderTrustScoreOption(t)}
        {renderCreditUsageOption(t)}
        {renderFavouritesOption(t)}
        {renderSavedAlertsOption(t, savedAlertsClick)}
        <PricingWrapper showSearch={showSearch}>
          <Divider />
          {renderPricingOption(t)}
        </PricingWrapper>
        <Divider />
        {renderAccountLabel(t)}
        {renderLoginOption(t, loginClick)}
        {renderSignupOption(t, signupClick)}
      </>
    )
  }

  const renderTenantMenu = () => {
    return (
      <>
        {renderManageLabel(t, 'logged-')}
        {renderProfileOption(t)}
        {renderChatOption(t, 'logged-')}
        {renderRentalsOption(t, 'logged-')}
        {renderFavouritesOption(t, 'logged-')}
        {renderSavedAlertsOption(t, savedAlertsClick, isLoggedIn)}
        <PricingWrapper showSearch={showSearch}>
          {renderPricingOption(t, 'logged-')}
        </PricingWrapper>
        <Divider />
        {renderAccountLabel(t, 'logged-')}
        {renderTenantSettingsOption(t)}
        {renderLogoutOption(t, logoutClick)}
      </>
    )
  }

  const renderLandlordMenu = () => {
    return (
      <>
        {renderManageLabel(t, 'logged-')}
        {renderChatOption(t, 'logged-')}
        {renderListingsOption(t, 'logged-')}
        {renderTenantsOption(t, 'logged-')}
        {renderTrustScoreOption(t, 'logged-')}
        {renderCreditUsageOption(t, 'logged-')}
        <PricingWrapper showSearch={showSearch}>
          {renderPricingOption(t, 'logged-')}
        </PricingWrapper>
        <Divider />
        {renderAccountLabel(t, 'logged-')}
        {renderProfileOption(t)}
        {renderSavedAlertsOption(t, savedAlertsClick, isLoggedIn)}
        {renderTeamOption(t, hasEssentialsPlan)}
        {renderLandlordSettingsOption(t)}
        {renderLogoutOption(t, logoutClick)}
      </>
    )
  }

  const getMenuOption = () => {
    if (hasSession) return isTenant ? renderTenantMenu() : renderLandlordMenu()
    return renderPublicMenu()
  }

  const menuText = t('b.header.view.menu.button', 'Menu')

  return (
    <>
      <Wrapper
        aria-haspopup="true"
        aria-controls="header-menu-popper"
        aria-expanded={isOpen}
        ref={setReferenceElement}
        onClick={handleClick}
        aria-label={menuText}
        disabled={isOpen}
        id="btn-menu"
      >
        <HamburgerMenuIcon height={15} width={15} />
        {displayMobile ? (
          <StyledChatNotification top="-20px" right="-13px" />
        ) : (
          <>
            <Avatar
              width={30}
              height={30}
              left={10}
              location={avatarUrl}
              shadow
            />
            <StyledChatNotification top="-21px" left="-9px" />
          </>
        )}
      </Wrapper>
      {isMounted
        ? ReactDOM.createPortal(
            isOpen ? (
              !aboveIpadMiniMax ? (
                <Sidebar
                  changeIsOpenToFalse={changeIsOpenToFalse}
                  changeSetStateOfSidebar={changeSetStateOfSidebar}
                  stateOfSidebar={stateOfSidebar}
                  isOpen={isOpen}
                  hasSession={hasSession}
                  hasEssentialsPlan={hasEssentialsPlan}
                  signupClick={signupClick}
                  loginClick={loginClick}
                  loginClickRedirectChat={loginClickRedirectChat}
                  loginClickRedirectListings={loginClickRedirectListings}
                  logoutClick={logoutClick}
                  closeSidebar={closeSidebar}
                />
              ) : (
                <Dropdown
                  style={styles.popper}
                  ref={setPopperElement}
                  className="header-menu-dropdown"
                  {...attributes.popper}
                >
                  {getMenuOption()}
                </Dropdown>
              )
            ) : null,
            document.getElementById('header-menu-popper')
          )
        : null}
    </>
  )
}

export default HeaderMenu
